"use client";

import { useEffect, useMemo, useState } from "react";
import { useSiteSettings } from "@/components/site-settings-provider";

type GrecaptchaInstance = {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

function getGrecaptcha() {
  return (window as Window & { grecaptcha?: GrecaptchaInstance }).grecaptcha;
}

export function useRecaptchaV3() {
  const { settings } = useSiteSettings();
  const [isReady, setIsReady] = useState(false);

  const enabled = useMemo(
    () => settings.captcha.enabled && settings.captcha.provider === "google_recaptcha_v3" && Boolean(settings.captcha.siteKey),
    [settings.captcha.enabled, settings.captcha.provider, settings.captcha.siteKey]
  );

  useEffect(() => {
    if (!enabled) {
      setIsReady(false);
      return;
    }

    const siteKey = settings.captcha.siteKey;
    const existing = document.querySelector(`script[data-recaptcha-site-key="${siteKey}"]`) as HTMLScriptElement | null;
    const markReady = () => {
      const grecaptcha = getGrecaptcha();
      if (!grecaptcha) {
        return;
      }

      grecaptcha.ready(() => setIsReady(true));
    };

    if (existing) {
      markReady();
      return;
    }

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.dataset.recaptchaSiteKey = siteKey;
    script.onload = markReady;
    document.head.appendChild(script);
  }, [enabled, settings.captcha.siteKey]);

  const execute = async (action: string) => {
    if (!enabled) {
      return null;
    }

    const grecaptcha = getGrecaptcha();
    if (!grecaptcha) {
      throw new Error("Captcha is still loading. Please try again.");
    }

    return new Promise<string>((resolve, reject) => {
      grecaptcha.ready(async () => {
        try {
          const token = await grecaptcha.execute(settings.captcha.siteKey, { action });
          resolve(token);
        } catch {
          reject(new Error("Captcha verification failed. Please try again."));
        }
      });
    });
  };

  return { enabled, isReady, execute };
}